'use strict';
/* Karmagick v1.0.4 — il cielo vero: fasi della luna, segno della luna, solstizi ed equinozi, feste
   della Ruota dell'anno. Lo usano la sezione della luna della pagina iniziale (kg-home-luna), la pagina
   della luna (page.luna) e le forme delle stelle (kg-forme.js).
   Tutto si calcola nel browser: le pagine di Shopify restano in cache anche per giorni.
   - Fasi principali: Meeus, «Astronomical Algorithms», cap. 49 (con le 14 correzioni planetarie).
   - Longitudine della luna: Meeus cap. 47 (i 59 termini principali della tavola 47.A) più la nutazione
     (cap. 22, termini principali): serve per il segno, che è quello tropicale.
   - Sole: Meeus cap. 25 (precisione ridotta), per la luce e l'elongazione.
   - Solstizi ed equinozi: Meeus cap. 27.
   Controllato il 2026-10-29 contro USNO (fasi 2026-2030, stagioni 2026-2027) e JPL Horizons
   (longitudine apparente della luna ogni 12 ore da ottobre 2026 a dicembre 2027): vedi
   reports/luna-2026-10-29/verifica/. Le ore escono in UTC (oggetti Date): le scrive Intl nel fuso
   del visitatore. */
(() => {
  const RAD = Math.PI / 180;
  const sin = (g) => Math.sin(g * RAD), cos = (g) => Math.cos(g * RAD);
  const giro = (g) => ((g % 360) + 360) % 360;
  /* ΔT (TT − UT): 69 s nel 2025-2026 secondo l'IERS, cresce di meno di un secondo l'anno; per le ore
     al minuto basta così */
  const DT = 69.5 / 86400;
  const jdDi = (data) => data.getTime() / 864e5 + 2440587.5;
  const dataDi = (jd) => new Date((jd - 2440587.5) * 864e5);

  /* ── fasi principali (cap. 49): 0 nuova, 1 primo quarto, 2 piena, 3 ultimo quarto ── */
  const NUOVA = [
    [-0.4072, 0, 0, 1, 0], [0.17241, 1, 1, 0, 0], [0.01608, 0, 0, 2, 0], [0.01039, 0, 0, 0, 2], [0.00739, 1, -1, 1, 0], [-0.00514, 1, 1, 1, 0],
    [0.00208, 2, 2, 0, 0], [-0.00111, 0, 0, 1, -2], [-0.00057, 0, 0, 1, 2], [0.00056, 1, 1, 2, 0], [-0.00042, 0, 0, 3, 0], [0.00042, 1, 1, 0, 2],
    [0.00038, 1, 1, 0, -2], [-0.00024, 1, -1, 2, 0],
  ];
  const PIENA = [
    [-0.40614, 0, 0, 1, 0], [0.17302, 1, 1, 0, 0], [0.01614, 0, 0, 2, 0], [0.01043, 0, 0, 0, 2], [0.00734, 1, -1, 1, 0], [-0.00515, 1, 1, 1, 0],
    [0.00209, 2, 2, 0, 0], [-0.00111, 0, 0, 1, -2], [-0.00057, 0, 0, 1, 2], [0.00056, 1, 1, 2, 0], [-0.00042, 0, 0, 3, 0], [0.00042, 1, 1, 0, 2],
    [0.00038, 1, 1, 0, -2], [-0.00024, 1, -1, 2, 0],
  ];
  /* coda comune a nuova e piena: [coefficiente, M, M', F] */
  const CODA = [
    [-0.00007, 2, 1, 0], [0.00004, 0, 2, -2], [0.00004, 3, 0, 0], [0.00003, -1, 1, -2], [0.00003, 0, 2, 2], [-0.00003, 1, 1, 2],
    [0.00003, -1, 1, 2], [-0.00002, -1, 1, -2], [-0.00002, 1, 3, 0], [0.00002, 0, 4, 0],
  ];
  const QUARTO = [
    [-0.62801, 0, 0, 1, 0], [0.17172, 1, 1, 0, 0], [-0.01183, 1, 1, 1, 0], [0.00862, 0, 0, 2, 0], [0.00804, 0, 0, 0, 2], [0.00454, 1, -1, 1, 0],
    [0.00204, 2, 2, 0, 0], [-0.0018, 0, 0, 1, -2], [-0.0007, 0, 0, 1, 2], [-0.0004, 0, 0, 3, 0], [-0.00034, 1, -1, 2, 0], [0.00032, 1, 1, 0, 2],
    [0.00032, 1, 1, 0, -2], [-0.00028, 2, 2, 1, 0], [0.00027, 1, 1, 2, 0],
  ];
  const CODA_Q = [
    [-0.00005, -1, 1, -2], [0.00004, 0, 2, 2], [-0.00004, 1, 1, 2], [0.00004, -2, 1, 0], [0.00003, -1, 1, -2], [0.00003, 3, 0, 0],
    [0.00002, 0, 2, -2], [0.00002, -1, 1, 2], [-0.00002, 1, 3, 0],
  ];
  const PIANETI = [
    [0.000325, 299.77, 0.107408, -0.009173], [0.000165, 251.88, 0.016321], [0.000164, 251.83, 26.651886], [0.000126, 349.42, 36.412478],
    [0.00011, 84.66, 18.206239], [0.000062, 141.74, 53.303771], [0.00006, 207.14, 2.453732], [0.000056, 154.84, 7.30686],
    [0.000047, 34.52, 27.261239], [0.000042, 207.19, 0.121824], [0.00004, 291.34, 1.844379], [0.000037, 161.72, 24.198154],
    [0.000035, 239.56, 25.513099], [0.000023, 331.55, 3.592518],
  ];
  /* k intero = luna nuova; k + 0.25, + 0.5, + 0.75 le altre. Restituisce l'istante in UT */
  function faseK(k) {
    const T = k / 1236.85, T2 = T * T, T3 = T2 * T, T4 = T3 * T;
    let jde = 2451550.09766 + 29.530588861 * k + 0.00015437 * T2 - 0.00000015 * T3 + 0.00000000073 * T4;
    const E = 1 - 0.002516 * T - 0.0000074 * T2;
    const M = 2.5534 + 29.1053567 * k - 0.0000014 * T2 - 0.00000011 * T3;
    const Mp = 201.5643 + 385.81693528 * k + 0.0107582 * T2 + 0.00001238 * T3 - 0.000000058 * T4;
    const F = 160.7108 + 390.67050284 * k - 0.0016118 * T2 - 0.00000227 * T3 + 0.000000011 * T4;
    const Om = 124.7746 - 1.56375588 * k + 0.0020672 * T2 + 0.00000215 * T3;
    const q = Math.round((k - Math.floor(k)) * 4) % 4;
    const somma = (tab, coda) => {
      let s = 0;
      for (const [c, e, m, mp, f] of tab) s += c * (e === 2 ? E * E : e === 1 ? E : 1) * sin(m * M + mp * Mp + f * F);
      for (const [c, m, mp, f] of coda) s += c * sin(m * M + mp * Mp + f * F);
      return s - 0.00017 * sin(Om);
    };
    if (q === 0) jde += somma(NUOVA, CODA);
    else if (q === 2) jde += somma(PIENA, CODA);
    else {
      jde += somma(QUARTO, CODA_Q);
      const W = 0.00306 - 0.00038 * E * cos(M) + 0.00026 * cos(Mp) - 0.00002 * cos(Mp - M) + 0.00002 * cos(Mp + M) + 0.00002 * cos(2 * F);
      jde += q === 1 ? W : -W;
    }
    for (const [c, a, b, t2 = 0] of PIANETI) jde += c * sin(a + b * k + t2 * T2);
    return dataDi(jde - DT);
  }
  const TIPI = ['new', 'first_quarter', 'full', 'last_quarter'];
  /* le fasi principali fra due date, in ordine */
  function fasiTra(da, a) {
    const anni = (d) => (d.getTime() - Date.UTC(2000, 0, 6)) / (365.25 * 864e5);
    let k = Math.floor(anni(da) * 12.3685) - 1;
    const out = [];
    for (; ; k += 0.25) {
      const quando = faseK(k);
      if (quando > a) break;
      if (quando >= da) out.push({ tipo: TIPI[Math.round((k - Math.floor(k)) * 4) % 4], quando, k });
    }
    return out;
  }

  /* ── la luna (cap. 47): longitudine apparente, in gradi ── */
  /* [D, M, M', F, Σl × 10⁻⁶ gradi] */
  const LON = [
    [0, 0, 1, 0, 6288774], [2, 0, -1, 0, 1274027], [2, 0, 0, 0, 658314], [0, 0, 2, 0, 213618], [0, 1, 0, 0, -185116], [0, 0, 0, 2, -114332],
    [2, 0, -2, 0, 58793], [2, -1, -1, 0, 57066], [2, 0, 1, 0, 53322], [2, -1, 0, 0, 45758], [0, 1, -1, 0, -40923], [1, 0, 0, 0, -34720],
    [0, 1, 1, 0, -30383], [2, 0, 0, -2, 15327], [0, 0, 1, 2, -12528], [0, 0, 1, -2, 10980], [4, 0, -1, 0, 10675], [0, 0, 3, 0, 10034],
    [4, 0, -2, 0, 8548], [2, 1, -1, 0, -7888], [2, 1, 0, 0, -6766], [1, 0, -1, 0, -5163], [1, 1, 0, 0, 4987], [2, -1, 1, 0, 4036],
    [2, 0, 2, 0, 3994], [4, 0, 0, 0, 3861], [2, 0, -3, 0, 3665], [0, 1, -2, 0, -2689], [2, 0, -1, 2, -2602], [2, -1, -2, 0, 2390],
    [1, 0, 1, 0, -2348], [2, -2, 0, 0, 2236], [0, 1, 2, 0, -2120], [0, 2, 0, 0, -2069], [2, -2, -1, 0, 2048], [2, 0, 1, -2, -1773],
    [2, 0, 0, 2, -1595], [4, -1, -1, 0, 1215], [0, 0, 2, 2, -1110], [3, 0, -1, 0, -892], [2, 1, 1, 0, -810], [4, -1, -2, 0, 759],
    [0, 2, -1, 0, -713], [2, 2, -1, 0, -700], [2, 1, -2, 0, 691], [2, -1, 0, -2, 596], [4, 0, 1, 0, 549], [0, 0, 4, 0, 537],
    [4, -1, 0, 0, 520], [1, 0, -2, 0, -487], [2, 1, 0, -2, -399], [0, 0, 2, -2, -381], [1, 1, 1, 0, 351], [3, 0, -2, 0, -340],
    [4, 0, -3, 0, 330], [2, -1, 2, 0, 327], [0, 2, 1, 0, -323], [1, 1, -1, 0, 299], [2, 0, 3, 0, 294],
  ];
  function lunaLon(data) {
    const T = (jdDi(data) + DT - 2451545) / 36525, T2 = T * T, T3 = T2 * T, T4 = T3 * T;
    const Lp = 218.3164477 + 481267.88123421 * T - 0.0015786 * T2 + T3 / 538841 - T4 / 65194000;
    const D = 297.8501921 + 445267.1114034 * T - 0.0018819 * T2 + T3 / 545868 - T4 / 113065000;
    const M = 357.5291092 + 35999.0502909 * T - 0.0001536 * T2 + T3 / 24490000;
    const Mp = 134.9633964 + 477198.8675055 * T + 0.0087414 * T2 + T3 / 69699 - T4 / 14712000;
    const F = 93.272095 + 483202.0175233 * T - 0.0036539 * T2 - T3 / 3526000 + T4 / 863310000;
    const E = 1 - 0.002516 * T - 0.0000074 * T2;
    let s = 0;
    for (const [d, m, mp, f, c] of LON) s += c * (Math.abs(m) === 2 ? E * E : m ? E : 1) * sin(d * D + m * M + mp * Mp + f * F);
    const A1 = 119.75 + 131.849 * T, A2 = 53.09 + 479264.29 * T;
    s += 3958 * sin(A1) + 1962 * sin(Lp - F) + 318 * sin(A2);
    /* nutazione in longitudine (cap. 22, termini principali) */
    const Om = 125.04452 - 1934.136261 * T, Ls = 280.4665 + 36000.7698 * T;
    const dPsi = (-17.2 * sin(Om) - 1.32 * sin(2 * Ls) - 0.23 * sin(2 * Lp) + 0.21 * sin(2 * Om)) / 3600;
    return giro(Lp + s / 1e6 + dPsi);
  }

  /* ── il sole (cap. 25, precisione ridotta): longitudine apparente ── */
  function soleLon(data) {
    const T = (jdDi(data) + DT - 2451545) / 36525;
    const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
    const M = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;
    const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * sin(M) + (0.019993 - 0.000101 * T) * sin(2 * M) + 0.000289 * sin(3 * M);
    const Om = 125.04 - 1934.136 * T;
    return giro(L0 + C - 0.00569 - 0.00478 * sin(Om));
  }

  /* ── solstizi ed equinozi (cap. 27): 0 primavera, 1 estate, 2 autunno, 3 inverno ── */
  const STAGIONI = [
    [2451623.80984, 365242.37404, 0.05169, -0.00411, -0.00057], [2451716.56767, 365241.62603, 0.00325, 0.00888, -0.0003],
    [2451810.21715, 365242.01767, -0.11575, 0.00337, 0.00078], [2451900.05952, 365242.74049, -0.06223, -0.00823, 0.00032],
  ];
  const PERIODICI = [
    [485, 324.96, 1934.136], [203, 337.23, 32964.467], [199, 342.08, 20.186], [182, 27.85, 445267.112], [156, 73.14, 45036.886],
    [136, 171.52, 22518.443], [77, 222.54, 65928.934], [74, 296.72, 3034.906], [70, 243.58, 9037.513], [58, 119.81, 33718.147],
    [52, 297.17, 150.678], [50, 21.02, 2281.226], [45, 247.54, 29929.562], [44, 325.15, 31555.956], [29, 60.93, 4443.417],
    [18, 155.12, 67555.328], [17, 288.79, 4562.452], [16, 198.04, 62894.029], [14, 199.76, 31436.921], [12, 95.39, 14577.848],
    [12, 287.11, 31931.756], [12, 320.81, 34777.259], [9, 227.73, 1222.114], [8, 15.45, 16859.074],
  ];
  function stagione(anno, i) {
    const Y = (anno - 2000) / 1000, [a, b, c, d, e] = STAGIONI[i];
    const jde0 = a + b * Y + c * Y * Y + d * Y ** 3 + e * Y ** 4;
    const T = (jde0 - 2451545) / 36525;
    const W = 35999.373 * T - 2.47;
    const dl = 1 + 0.0334 * cos(W) + 0.0007 * cos(2 * W);
    let S = 0;
    for (const [A, B, C] of PERIODICI) S += A * cos(B + C * T);
    return dataDi(jde0 + (0.00001 * S) / dl - DT);
  }

  /* ── dove sta la luna adesso ── */
  const SEGNI = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];
  const SINODICO = 29.530588853;
  /* l'istante (fra a e b) in cui f passa per zero, con f(a) e f(b) di segno opposto */
  function zero(f, a, b) {
    let fa = f(a);
    for (let i = 0; i < 40 && b - a > 1000; i++) {
      const m = a + (b - a) / 2, fm = f(m);
      if ((fm < 0) === (fa < 0)) { a = m; fa = fm; } else b = m;
    }
    return new Date((a + b) / 2);
  }
  const scarto = (g) => ((g + 540) % 360) - 180;
  /* il segno della luna a una data e quando c'è entrata e ne uscirà (la luna fa 30° in circa 2,5 giorni) */
  function segnoLuna(data) {
    const t = data.getTime(), lon = lunaLon(data), s = Math.floor(lon / 30);
    const bordo = (g) => (x) => scarto(lunaLon(new Date(x)) - g);
    const ora = 36e5;
    let a = t;
    while (scarto(lunaLon(new Date(a)) - s * 30) > 0 && a > t - 4 * 864e5) a -= 6 * ora;
    let b = t;
    while (scarto(lunaLon(new Date(b)) - (s + 1) * 30) < 0 && b < t + 4 * 864e5) b += 6 * ora;
    return { segno: SEGNI[s], indice: s, lon, entrata: zero(bordo(s * 30), a, a + 6 * ora), uscita: zero(bordo((s + 1) * 30), b - 6 * ora, b) };
  }
  /* luce, età e fase a una data. Il nome della fase segue l'uso degli almanacchi: il giorno (del visitatore)
     in cui cade l'istante della nuova, del primo quarto, della piena o dell'ultimo quarto porta quel nome;
     gli altri giorni sono falce o gibbosa, crescente o calante. */
  const CHIAVI = ['new', 'waxing_crescent', 'first_quarter', 'waxing_gibbous', 'full', 'waning_gibbous', 'last_quarter', 'waning_crescent'];
  function inizioGiorno(data) { const d = new Date(data); d.setHours(0, 0, 0, 0); return d; }
  function fineGiorno(data) { const d = inizioGiorno(data); d.setDate(d.getDate() + 1); return d; }
  function luna(data = new Date()) {
    const elong = giro(lunaLon(data) - soleLon(data));
    const luce = (1 - cos(elong)) / 2;
    const vicine = fasiTra(new Date(data.getTime() - 32 * 864e5), new Date(data.getTime() + 32 * 864e5));
    const ultimaNuova = vicine.filter((f) => f.tipo === 'new' && f.quando <= data).pop();
    const eta = ultimaNuova ? (data - ultimaNuova.quando) / 864e5 : (elong / 360) * SINODICO;
    const g0 = inizioGiorno(data), g1 = fineGiorno(data);
    const oggi = vicine.find((f) => f.quando >= g0 && f.quando < g1);
    const chiave = oggi ? oggi.tipo : CHIAVI[elong < 90 ? 1 : elong < 180 ? 3 : elong < 270 ? 5 : 7];
    const prossima = (tipo) => vicine.find((f) => f.tipo === tipo && f.quando > data)?.quando || null;
    return {
      data, elong, luce, eta, chiave, indice: CHIAVI.indexOf(chiave), cresce: elong < 180, esatta: oggi || null,
      /* angolo per il disegno: 0 nuova, π piena (come kg-forme) */
      angolo: elong * RAD,
      nuova: prossima('new'), piena: prossima('full'), primoQuarto: prossima('first_quarter'), ultimoQuarto: prossima('last_quarter'),
      ultimaNuova: ultimaNuova?.quando || null,
    };
  }

  /* ── la Ruota dell'anno (emisfero nord): i solstizi e gli equinozi all'istante vero, le feste di mezzo
     nelle date della tradizione (sera della vigilia compresa: Samhain è la notte fra il 31 ottobre e il 1° novembre) ── */
  const FESTE = [
    { chiave: 'imbolc', mese: 2, giorno: 1 }, { chiave: 'ostara', stagione: 0 }, { chiave: 'beltane', mese: 5, giorno: 1 },
    { chiave: 'litha', stagione: 1 }, { chiave: 'lughnasadh', mese: 8, giorno: 1 }, { chiave: 'mabon', stagione: 2 },
    { chiave: 'samhain', mese: 10, giorno: 31 }, { chiave: 'yule', stagione: 3 },
  ];
  function feste(anno) {
    return FESTE.map((f) => {
      if (f.stagione !== undefined) return { chiave: f.chiave, quando: stagione(anno, f.stagione), esatta: true };
      return { chiave: f.chiave, quando: new Date(anno, f.mese - 1, f.giorno), esatta: false };
    });
  }
  /* la prossima festa da oggi (oggi compreso: il giorno della festa vale fino a sera) */
  function prossimaFesta(data = new Date()) {
    const g0 = inizioGiorno(data);
    const tutte = [...feste(data.getFullYear()), ...feste(data.getFullYear() + 1)];
    return tutte.find((f) => inizioGiorno(f.quando) >= g0);
  }
  /* dove sta l'anno sulla ruota: 0 al solstizio d'inverno, una festa ogni 45° circa */
  function annoSullaRuota(data = new Date()) { return giro(soleLon(data) - 270) / 360; }

  /* ── le ore di luce a una latitudine (2026-10-29, per la ruota dell'anno): declinazione del sole dalla sua
     longitudine apparente, alba e tramonto a −0°50′ (rifrazione e mezzo disco). A 45° nord, contro USNO su
     98 giorni del 2026-2027: scarto medio 0,3 minuti, peggiore 1 (reports/ruota-2026-10-29/verifica/luce.mjs).
     oreDiLuceSullaRuota fa lo stesso per un punto della ruota (sole a 270° + 360° · u), come lo shader. ── */
  function oreDaLongitudine(lon, lat) {
    const dec = Math.asin(sin(23.4367) * sin(lon));
    const c = (sin(-0.8333) - sin(lat) * Math.sin(dec)) / (cos(lat) * Math.cos(dec));
    return (2 * Math.acos(Math.max(-1, Math.min(1, c)))) / RAD / 15;
  }
  const oreDiLuce = (data = new Date(), lat = 45) => oreDaLongitudine(soleLon(data), lat);
  const oreDiLuceSullaRuota = (u, lat = 45) => oreDaLongitudine(270 + 360 * u, lat);

  /* ── le lune piene dell'anno, coi nomi degli almanacchi: la luna del raccolto è la piena più vicina
     all'equinozio d'autunno, quella del cacciatore la successiva; la luna blu (del calendario) è la seconda
     piena di un mese ── */
  const NOMI_MESE = ['wolf', 'snow', 'worm', 'pink', 'flower', 'strawberry', 'buck', 'sturgeon', 'corn', 'hunter', 'beaver', 'cold'];
  function lunePiene(da, a) {
    const piene = fasiTra(new Date(da.getTime() - 400 * 864e5), new Date(a.getTime() + 60 * 864e5)).filter((f) => f.tipo === 'full');
    const anni = [...new Set(piene.map((p) => p.quando.getFullYear()))];
    const raccolto = new Set(), cacciatore = new Set();
    for (const y of anni) {
      const eq = stagione(y, 2);
      let i = 0, best = Infinity;
      piene.forEach((p, j) => { const d = Math.abs(p.quando - eq); if (d < best) { best = d; i = j; } });
      if (best < 20 * 864e5) { raccolto.add(piene[i].k); if (piene[i + 1]) cacciatore.add(piene[i + 1].k); }
    }
    return piene.filter((p) => p.quando >= da && p.quando <= a).map((p) => {
      const m = p.quando.getMonth(), y = p.quando.getFullYear();
      const stessoMese = piene.filter((q) => q.quando.getMonth() === m && q.quando.getFullYear() === y);
      const blu = stessoMese.length > 1 && stessoMese[1].k === p.k;
      let nome = NOMI_MESE[m];
      if (raccolto.has(p.k)) nome = 'harvest';
      else if (cacciatore.has(p.k)) nome = 'hunter';
      return { quando: p.quando, nome, blu };
    });
  }

  globalThis.KG_CIELO = { faseK, fasiTra, lunaLon, soleLon, stagione, segnoLuna, luna, feste, prossimaFesta, annoSullaRuota, oreDiLuce, oreDiLuceSullaRuota, lunePiene, SEGNI, CHIAVI, SINODICO, inizioGiorno, fineGiorno };
})();
